import { mix } from 'polished';
import { useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { useFeature, useLocalize, useViewer, useViewerRoles } from '@treams/treams-hooks';
import { ChevronRightIcon } from '@treams/treams-icons';
import {
	AnalyticsEngagementOpenQuestion,
	AnalyticsEngagementOpenQuestionAnswer,
	AnalyticsEngagementQuestionScoreTrend,
	FeatureName,
	useEngagementAnswerWithRepliesQuery,
} from '@treams/treams-network';
import { IconButton, Paragraph, tokens } from '@treams/treams-ui';

import { EngagementDeltaTooltip } from './EngagementDeltaTooltip';
import EngagementQuestionButton from './EngagementQuestionButton';

const Score = styled(Box)<{ value?: number }>`
	background: ${(props) =>
		typeof props.value === 'number'
			? props.value >= 0.5
				? mix(props.value * 2 - 1, props.theme.colors.green, props.theme.colors.white)
				: mix(props.value * 2, props.theme.colors.white, props.theme.colors.red)
			: tokens.surface.secondary};
	width: 26px;
	height: 26px;
	flex-grow: 0;
	flex-shrink: 0;
	margin-right: 4px;
	border-radius: 13px;
	font-size: 12px;
	font-weight: bold;
	text-align: center;
	line-height: 26px;
`;

const QuestionContainer = styled(Flex)<{ first?: boolean }>`
	padding: 10px 12px;

	${(props) =>
		!props.first &&
		`
		border-top: 1px solid ${tokens.border.subtle};
	`}
`;

function EngagementQuestionScore(props: { question: AnalyticsEngagementQuestionScoreTrend }) {
	const score = props.question.datapoints[props.question.datapoints.length - 1]?.score;

	return (
		<>
			<Score value={typeof score === 'number' ? (score - 1) / 9 : undefined}>
				{score?.toFixed(1) ?? '-'}
			</Score>
			{typeof props.question.delta !== 'number' ? (
				<Box width={56} flexGrow={0} flexShrink={0} />
			) : (
				<EngagementDeltaTooltip delta={props.question.delta} />
			)}
		</>
	);
}

const EngagementQuestionAnswerContent = styled(Box)`
	border-radius: 5px;
	background: ${tokens.surface.secondary};
	overflow-wrap: break-word;
	hyphens: auto;
`;

function EngagementQuestionAnswer({ answer }: { answer: AnalyticsEngagementOpenQuestionAnswer }) {
	const useEngagementReply = useFeature(FeatureName.EngagementReply);

	const viewer = useViewer();
	const userRoles = useViewerRoles();
	const isSuper = userRoles.isWorkspaceSuperuser || userRoles.isEngagementOfficer;

	const managerID = isSuper ? null : viewer!.id;

	const { data } = useEngagementAnswerWithRepliesQuery({
		variables: {
			id: answer.id,
			managerID,
		},
	});

	return (
		<Flex mt={2} alignItems="center">
			<EngagementQuestionAnswerContent p={3} flexGrow={1}>
				{answer.text}
			</EngagementQuestionAnswerContent>

			{useEngagementReply && (
				<EngagementQuestionButton
					answerID={answer.id}
					repliesCount={data?.openQuestionAnswerWithReplies.replies.length}
				/>
			)}
		</Flex>
	);
}

function EngagementQuestionAnswers(props: { answers: AnalyticsEngagementOpenQuestionAnswer[] }) {
	return (
		<Box mt={3}>
			{props.answers.map((answer) => (
				<EngagementQuestionAnswer key={answer.id} answer={answer} />
			))}
		</Box>
	);
}

export default function EngagementQuestion(props: {
	question: AnalyticsEngagementQuestionScoreTrend | AnalyticsEngagementOpenQuestion;
	first?: boolean;
}) {
	const { localize } = useLocalize();
	const [isToggled, setIsToggled] = useState(false);

	return (
		<QuestionContainer alignItems="center" first={props.first}>
			{'datapoints' in props.question ? (
				<EngagementQuestionScore question={props.question} />
			) : (
				<Box flexGrow={0} flexShrink={0} />
			)}
			{props.question.__typename === 'AnalyticsEngagementOpenQuestion' ? (
				<>
					<IconButton
						type="plain"
						icon={<StyledChevronIcon $opened={isToggled} />}
						onClick={() => setIsToggled(!isToggled)}
						mr={2}
						alignSelf="start"
						flexShrink={0}
					/>
					<Box py="5px" flexGrow={1}>
						<Paragraph>{localize(props.question.question.title).text}</Paragraph>

						{isToggled && 'answers' in props.question && (
							<EngagementQuestionAnswers answers={props.question.answers} />
						)}
					</Box>
				</>
			) : (
				<Box py="5px">
					<Paragraph>{localize(props.question.question.title).text}</Paragraph>
				</Box>
			)}
		</QuestionContainer>
	);
}

function ChevronIconBox() {
	return <ChevronRightIcon />;
}

const StyledChevronIcon = styled(ChevronIconBox)<{ $opened: boolean }>`
	${({ $opened }) => css`
		transform: rotate(${$opened ? 90 : 0}deg);
	`}
`;
